import { prisma } from 'lib/prisma';
import Link from 'next/link';
import { GetStaticPaths, GetStaticProps, GetStaticPropsResult } from 'next';
import {
  CategoryWithRelations,
  ProductWithRelations,
  ReducedAccount,
  NavbarCategory,
  WithSEOProps,
} from 'Types';
import MenuCategory from 'components/MenuCategory';
import MenuNavBar from 'components/NavBars/MenuNavBar';
import { useState } from 'react';
import { useMenuContext } from 'context/MenuContext';
import { getTranslation } from 'dictionary';
import { useUserContext } from 'context/UserContext';
import { ToastContainer } from 'react-toastify';
import MenuCarousel from 'components/MenuCarousel';
import SideBar from 'components/SideBar';
import {
  getAccountWithRelations,
  reduceAccount,
} from 'helpers/api/getReducedAccount';
import { parse } from 'helpers';

interface Props {
  categories: CategoryWithRelations[];
  categoriesTitles: NavbarCategory[];
  featuredProducts: ProductWithRelations[];
  account: ReducedAccount;
  bannerInfo: string | null;
  description: string | null;
}
const Page: React.FC<Props> = ({
  categories = [],
  categoriesTitles = [],
  featuredProducts = [],
  bannerInfo,
  description,
}) => {
  const [isSideBarOpen, setIsSideBarOpen] = useState(false);
  const { language } = useUserContext();
  const { whatsapp, facebook, instagram } = useMenuContext();

  const whatsappMessageUrl = `https://wa.me/${whatsapp}`;
  const facebookUrl = `https://facebook.com/${facebook}`;
  const instagramUrl = `https://instagram.com/${instagram}`;

  return (
    <div className="md:h-screen md:flex md:justify-center md:items-center md:bg-gray-50">
      <div className="md:w-[390px] md:mx-auto md:h-[90vh] md:overflow-scroll md:border-4 md:border-black md:rounded-2xl">
        <MenuNavBar
          description={description || ''}
          openSideBar={() => setIsSideBarOpen(true)}
        />
        <main id="app_main">
          <SideBar
            categories={categoriesTitles}
            isOpen={isSideBarOpen}
            handleClose={() => setIsSideBarOpen(false)}
          />
          <div className="bg-white">
            {bannerInfo ? (
              <h3 className="border-b border-solid border-gray-200 py-2 px-4 text-sm">
                {bannerInfo}
              </h3>
            ) : null}
            {featuredProducts.length > 0 ? (
              <MenuCarousel featuredProducts={featuredProducts} />
            ) : null}
            <div className="grid grid-cols-1 justify-items-center ml-auto mr-auto sm:overflow-scroll px-4">
              {categories.length ? (
                categories.map((category, index) => (
                  <MenuCategory
                    index={index}
                    category={category}
                    key={category.id}
                  />
                ))
              ) : (
                <div> {getTranslation('menu', 'EMPTY_STORE', language)}</div>
              )}
            </div>
          </div>
        </main>

        <footer className="border-t border-solid border-gray-200 space-y-4 px-4 py-8">
          <div className="flex gap-2 items-center justify-center">
            {(whatsapp || facebook || instagram) && (
              <span>{getTranslation('menu', 'SOCIAL_MEDIA', language)}</span>
            )}
            {whatsapp && (
              <a href={whatsappMessageUrl} target="_blank" rel="noreferrer">
                <svg
                  className="h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 448 512"
                >
                  <title>Whatsapp icon</title>
                  <path
                    fill="currentColor"
                    d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"
                  ></path>
                </svg>
              </a>
            )}
            {facebook && (
              <a href={facebookUrl} target="_blank" rel="noreferrer">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  // viewBox="0 0 320 512"
                  viewBox="0 0 448 512"
                  className="h-6 w-6"
                >
                  <title>Facebook icon</title>
                  <path
                    fill="currentColor"
                    d="M279.14 288l14.22-92.66h-88.91v-60.13c0-25.35 12.42-50.06 52.24-50.06h40.42V6.26S260.43 0 225.36 0c-73.22 0-121.08 44.38-121.08 124.72v70.62H22.89V288h81.39v224h100.17V288z"
                  />
                </svg>
              </a>
            )}
            {instagram && (
              <a href={instagramUrl} target="_blank" rel="noreferrer">
                <svg
                  height="100%"
                  viewBox="0 0 24 24"
                  width="100%"
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                >
                  <title>Instagram icon</title>

                  <path d="M17 2a5 5 0 015 5v10a5 5 0 01-5 5H7a5 5 0 01-5-5V7a5 5 0 015-5h10m0-2H7a7 7 0 00-7 7v10a7 7 0 007 7h10a7 7 0 007-7V7a7 7 0 00-7-7z"></path>
                  <path d="M18.5 7A1.5 1.5 0 1120 5.5 1.5 1.5 0 0118.5 7zM12 8a4 4 0 11-4 4 4 4 0 014-4m0-2a6 6 0 106 6 6 6 0 00-6-6z"></path>
                </svg>
              </a>
            )}
          </div>
          <div className="text-center text-gray-900 font-light lowercase">
            {getTranslation('menu', 'CREATED_WITH', language)}
            <Link
              href="/"
              className="font-rochester text-2xl px-1 logo font-bold capitalize"
            >
              Simple
            </Link>
          </div>
        </footer>
        <ToastContainer
          position="top-center"
          autoClose={2000}
          hideProgressBar
          closeOnClick
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </div>
    </div>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const accounts = await prisma.account.findMany({
    where: {
      slug: {
        not: null,
      },
    },
  });
  const urls = accounts.map((account) => `/${account.slug}`);

  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async ({
  params,
}): Promise<GetStaticPropsResult<WithSEOProps<Props>>> => {
  if (typeof params?.store_name !== 'string')
    return {
      notFound: true,
    };
  const account = await getAccountWithRelations(params.store_name);
  if (!account?.slug) {
    return {
      notFound: true,
    };
  }

  const [featuredProducts, categories]: [
    ProductWithRelations[],
    CategoryWithRelations[]
  ] = await Promise.all([
    prisma.product.findMany({
      where: {
        userId: account.id,
        featured: true,
        removed: false,
        status: 'available',
      },
      include: {
        images: true,
        variants: {
          where: {
            removed: false,
          },
          orderBy: { position: 'asc' },
          include: {
            options: {
              where: {
                NOT: { removed: true },
              },
              orderBy: { position: 'asc' },
            },
          },
        },
        extras: {
          include: {
            extraItems: {
              include: {
                product: {
                  include: {
                    images: true,
                  },
                },
              },
            },
          },
        },
      },
    }),
    prisma.category.findMany({
      where: {
        userId: account.id,
      },
      orderBy: {
        position: 'asc',
      },
      include: {
        productsOrder: {
          orderBy: {
            position: 'asc',
          },
          include: {
            product: {
              include: {
                images: {
                  orderBy: {
                    position: 'asc',
                  },
                },
                variants: {
                  where: {
                    removed: false,
                  },
                  orderBy: { position: 'asc' },
                  include: {
                    options: {
                      where: {
                        NOT: { removed: true },
                      },
                      orderBy: { position: 'asc' },
                    },
                  },
                },
                extras: {
                  include: {
                    extraItems: {
                      include: {
                        product: {
                          include: {
                            images: true,
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      },
    }),
  ]);

  const categoriesWithProducts = categories.filter(
    (c) => c.productsOrder.length > 0
  );

  const categoriesTitles = categories.map((c) => ({
    title: c.title,
    id: c.id,
  }));
  if (featuredProducts.length) {
    categoriesTitles.unshift({ title: 'Destacados', id: 'featured' });
  }

  return {
    props: {
      SEOTitle: `${account.business_name} , tienda online` || 'Tienda online',
      SEODescription:
        account.StoreDesign?.navbarDesign?.subtitle ||
        account.description ||
        account.banner_info ||
        'Tienda online',
      SEOImage: account.logo?.url || null,
      SEONavbarBackground:
        account.StoreDesign?.navbarDesign?.backgroundColor || '',
      description:
        account.StoreDesign?.navbarDesign?.subtitle || account.description,
      bannerInfo:
        account.StoreDesign?.bodyDesign?.bannerInfo || account.banner_info,
      account: reduceAccount(account),
      categories: parse(categoriesWithProducts),
      featuredProducts: parse(featuredProducts),
      categoriesTitles: categoriesTitles,
    },
  };
};
export default Page;
